import type { Tool } from "@ag-ui/client";
import type { FieldActionContext } from "@/features/catalog_case_review/shared/model/fieldActionContext";
import type { PageLocation } from "../shared/model/pageLocation";
import type { CaseReviewAgentBindings } from "../shared/model/caseReviewAgentBindings";
import type { WorkQueueAgentBindings } from "../shared/model/workQueueAgentBindings";
import { FRONTEND_TOOL_CODES, type FrontendToolCode, type FrontendToolName } from "./frontendToolNames";

/** What a frontend tool answered; the browser serialises it into the tool message the model reads next. */
export type FrontendToolResult =
  | ({ ok: true } & Record<string, unknown>)
  | { ok: false; code: FrontendToolCode; message: string };

/**
 * A string node limited to `values`. The ADK LangChain4j bridge drops `enum` on its way to the
 * model, so the description names the values as well; `enum` stays for any client that reads it.
 */
export function enumStringNode(values: readonly string[]): { type: "string"; enum: string[]; description: string } {
  return { type: "string", enum: [...values], description: `One of: ${values.join(", ")}.` };
}

/** A string node fixed to `value`: `const` is dropped by the bridge too, so the description says it. */
export function constStringNode(value: string): { type: "string"; const: string; description: string } {
  return { type: "string", const: value, description: `Always ${value}.` };
}

/** What an executor may reach: the screen as published, the screens' registered bindings, and the router. */
export interface FrontendToolEnvironment {
  location: PageLocation | null;
  caseReview: CaseReviewAgentBindings | null;
  workQueue: WorkQueueAgentBindings | null;
  navigate: (path: string) => Promise<void>;
  /** The run and call being answered; a proposal is recorded against them. */
  call: FrontendToolCall;
}

/** The call being answered, and the field action pinned to its turn, if any. */
export interface FrontendToolCall {
  runId: string;
  toolCallId: string;
  /** The field action the reviewer started this turn with; a proposal on a paired field must then set the pair. */
  fieldAction: FieldActionContext | null;
}

/** The arguments as the model sent them, parsed from the call's JSON. */
export type FrontendToolArgs = Record<string, unknown>;

/**
 * One tool the browser executes: its JSON-schema declaration for a given screen (or none, when the
 * screen does not offer it), and the executor that re-checks live state before acting.
 */
export interface FrontendTool {
  name: FrontendToolName;
  declaration: (location: PageLocation | null, caseReview: CaseReviewAgentBindings | null, workQueue: WorkQueueAgentBindings | null) => Tool | null;
  execute: (args: FrontendToolArgs, env: FrontendToolEnvironment) => Promise<FrontendToolResult> | FrontendToolResult;
}

export function refuse(code: FrontendToolCode, message: string): FrontendToolResult {
  return { ok: false, code, message };
}

/** A required string argument, or a refusal naming it. */
export function requiredString(args: FrontendToolArgs, name: string): string | FrontendToolResult {
  const value = args[name];
  return typeof value === "string" && value.trim() !== "" ? value
    : refuse(FRONTEND_TOOL_CODES.invalidValue, `${name} must be a non-empty string.`);
}

/** An optional string argument: undefined when absent or null, a refusal when present and not a string. */
export function optionalString(args: FrontendToolArgs, name: string): string | undefined | FrontendToolResult {
  const value = args[name];
  if (value === undefined || value === null) return undefined;
  return typeof value === "string" ? value : refuse(FRONTEND_TOOL_CODES.invalidValue, `${name} must be a string.`);
}

export function isRefusal(value: unknown): value is FrontendToolResult {
  return typeof value === "object" && value !== null && (value as { ok?: unknown }).ok === false;
}
